import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./chart-interactions.css";
import "./lib/i18n";
import { initConsentScripts } from "./lib/consent-scripts";
import { initOAuthReturnDiagnostics } from "./lib/oauthReturn";
import { vpSnap } from "./lib/viewportDiagnostics";
import ProductAnalyticsLifecycle from "./components/ProductAnalyticsLifecycle";
import { rememberPlayTwaSession } from "./lib/playReviewPrompt";

// Capture the clean document state before React renders after the narrowly
// scoped Google OAuth hard navigation.
vpSnap("before-react-bootstrap");
initOAuthReturnDiagnostics();
// Capture the original Android TWA referrer before any OAuth navigation.
rememberPlayTwaSession();

/**
 * Reveals the app only after React has actually committed. Rendered after
 * <App />, so its effect runs once the whole tree is committed to the DOM —
 * the static SEO fallback inside #root is gone by then and can never flash.
 */
function HydrationReveal() {
  useEffect(() => {
    document.documentElement.classList.remove("tl-prehydration");
    document.documentElement.classList.add("tl-app-mounted");

    const splash = document.getElementById("app-splash");
    if (splash) setTimeout(() => splash.remove(), 200);
  }, []);
  return null;
}

const root = document.getElementById("root")!;
createRoot(root).render(
  <>
    <App />
    <ProductAnalyticsLifecycle />
    <HydrationReveal />
  </>,
);
vpSnap("react-startup");

initConsentScripts();


// Register a minimal service worker so the browser can offer "Install app".
// Skip in dev, preview iframes, and Lovable preview hosts to avoid stale caches.
const pwaLog = (label: string, details?: unknown) => (window as any).__logPwaInstall?.(label, details);
const pwaState = (patch: Record<string, unknown>, eventName?: string) =>
  (window as any).__setPwaInstallState?.(patch, eventName);

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  const isInIframe = (() => {
    try { return window.self !== window.top; } catch { return true; }
  })();
  const host = window.location.hostname;
  const isPreviewHost =
    host.includes("id-preview--") || host.includes("lovableproject.com");

  if (!isInIframe && !isPreviewHost) {
    const registrationPromise =
      ((window as any).__pwaSwRegistrationPromise as Promise<ServiceWorkerRegistration> | undefined) ??
      navigator.serviceWorker.register("/sw.js");

    if (!(window as any).__pwaSwRegistrationPromise) {
      (window as any).__pwaSwRegistrationPromise = registrationPromise;
    }

    registrationPromise
      .then((registration) => {
        pwaState({ serviceWorkerRegistered: true }, "pwa-service-worker-state");
        pwaLog("service worker registered", registration.scope);
      })
      .catch((error) => {
        pwaState({ serviceWorkerRegistered: false, serviceWorkerError: String(error) }, "pwa-service-worker-state");
        pwaLog("service worker registration failed", String(error));
      });

    navigator.serviceWorker.ready
      .then((registration) => {
        pwaState({ serviceWorkerReady: true }, "pwa-service-worker-state");
        pwaLog("service worker ready", registration.scope);
      })
      .catch((error) => pwaLog("service worker ready failed", String(error)));
  } else {
    pwaLog("service worker registration skipped", { isInIframe, host });
    navigator.serviceWorker.getRegistrations().then((regs) =>
      regs.forEach((r) => r.unregister())
    );
  }
} else {
  pwaLog("service worker registration skipped", {
    hasServiceWorker: "serviceWorker" in navigator,
    prod: import.meta.env.PROD,
  });
}
